import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { formatDate as _fmtD, formatDateTime as _fmtDT } from '../../lib/format';
import { Search, Sparkles, Plus, ExternalLink, Loader2, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import EmployeePicker from '../../components/EmployeePicker';

export default function OpportunityFinder() {
  const [industries, setIndustries] = useState([]);
  const [scans, setScans] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [newIndustry, setNewIndustry] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingOpp, setConvertingOpp] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [scanProgress, setScanProgress] = useState('');
  const [enriching, setEnriching] = useState(null);
  const [enrichedData, setEnrichedData] = useState(null);
  const [showEnrichModal, setShowEnrichModal] = useState(false);

  const loadIndustries = async () => {
    try {
      const res = await apiGet('/crm/opportunity-finder/industries');
      setIndustries(res.industries || []);
      // Change 5: Do not auto-select any industries — user must pick manually
      setSelectedIndustries([]);
    } catch (err) {
      console.error('Failed to load industries:', err);
    }
  };

  const loadScans = async () => {
    try {
      const res = await apiGet('/crm/opportunity-finder/scans');
      setScans(res.scans || []);
    } catch (err) {
      console.error('Failed to load scans:', err);
    }
  };

  useEffect(() => {
    loadIndustries();
    loadScans();
  }, []);

  const handleScan = async () => {
    if (selectedIndustries.length === 0) {
      toast.error('Please select at least one industry');
      return;
    }
    setScanning(true);
    setScanProgress('Starting scan...');
    setScanResults(null);
    try {
      const params = selectedIndustries.map(i => `industries=${encodeURIComponent(i)}`).join('&');
      const { scan_id } = await apiPost(`/crm/opportunity-finder/scan?${params}`);
      loadScans(); // Show Running chip immediately in Previous Scans

      const POLL_MS = 4000;
      const MAX_POLLS = 150; // ~10 minutes
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_MS));
        const scan = await apiGet(`/crm/opportunity-finder/scans/${scan_id}`);
        setScanProgress(scan.progress || 'Scanning...');

        if (scan.status === 'done') {
          setScanResults(scan);
          toast.success(`Found ${scan.results_count} opportunities!`);
          loadScans();
          return;
        }
        if (scan.status === 'failed') {
          toast.error(scan.error || 'Scan failed. Please try again.');
          loadScans();
          return;
        }
      }
      toast.error('Scan is taking longer than expected. Check Previous Scans shortly.');
      loadScans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
      setScanProgress('');
    }
  };

  const handleAddIndustry = async (e) => {
    e.preventDefault();
    if (!newIndustry.trim()) return;
    try {
      await apiPost(`/crm/opportunity-finder/industries?name=${encodeURIComponent(newIndustry)}`);
      toast.success('Industry added');
      setNewIndustry('');
      loadIndustries();
    } catch (err) {
      toast.error('Failed to add industry');
    }
  };

  const convertToLead = async (scanId, index) => {
    setConvertingOpp({ scanId, index });
    setShowConvertModal(true);
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    if (!ownerName.trim()) {
      toast.error('Please select a lead owner');
      return;
    }
    try {
      const params = new URLSearchParams({
        scan_id: convertingOpp.scanId,
        opportunity_index: convertingOpp.index,
        owner: ownerName,
      });
      if (ownerId) params.set('owner_id', ownerId);
      await apiPost(`/crm/opportunity-finder/convert-to-lead?${params}`);
      toast.success('Converted to lead successfully!');
      // Change 3: Mark as converted in UI — hide from scan results
      setScanResults(prev => {
        if (!prev?.opportunities) return prev;
        const updated = [...prev.opportunities];
        if (updated[convertingOpp.index]) {
          updated[convertingOpp.index] = { ...updated[convertingOpp.index], converted: true };
        }
        return { ...prev, opportunities: updated };
      });
      setShowConvertModal(false);
      setOwnerName('');
      setOwnerId('');
      setConvertingOpp(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to convert to lead');
    }
  };

  const handleEnrich = async (opp) => {
    setEnriching(opp);
    try {
      const params = new URLSearchParams({
        client_name: opp.client_name || opp.company || 'Unknown',
        project_type: opp.project_type || 'Infrastructure',
        location: opp.location || 'India'
      });
      const data = await apiPost(`/crm/opportunity-finder/enrich?${params}`);
      setEnrichedData(data);
      setShowEnrichModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to enrich opportunity');
    } finally {
      setEnriching(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">CRM · AI-Powered</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
          Opportunity Finder <Sparkles size={28} className="text-amber-500" />
        </h1>
        <p className="text-sm text-slate-600 mt-1">Discover new construction projects in India using AI web intelligence</p>
      </div>

      {/* Scan Controls */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h2 className="font-display font-bold text-lg mb-4">Target Industries</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {industries.map(ind => (
            <button
              key={ind.id}
              onClick={() => {
                if (selectedIndustries.includes(ind.name)) {
                  setSelectedIndustries(selectedIndustries.filter(i => i !== ind.name));
                } else {
                  setSelectedIndustries([...selectedIndustries, ind.name]);
                }
              }}
              className={`px-4 py-2 rounded-sm border text-sm font-display transition-colors ${
                selectedIndustries.includes(ind.name)
                  ? 'bg-[#3b4872] text-white border-[#3b4872]'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'
              }`}
            >
              {ind.name}
            </button>
          ))}
        </div>
        
        <form onSubmit={handleAddIndustry} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newIndustry}
            onChange={(e) => setNewIndustry(e.target.value)}
            placeholder="Add new industry..."
            className="input flex-1 text-sm"
          />
          <button type="submit" className="btn-secondary flex items-center gap-2">
            <Plus size={16} /> Add
          </button>
        </form>

        <button
          onClick={handleScan}
          disabled={scanning || selectedIndustries.length === 0}
          className="btn-primary flex items-center gap-2 w-full justify-center"
          data-testid="start-scan-btn"
        >
          {scanning ? (
            <><Loader2 size={16} className="animate-spin" /> {scanProgress || 'Scanning...'}</>
          ) : (
            <><Search size={16} /> Start AI Scan</>
          )}
        </button>
        <p className="text-xs text-slate-500 mt-2">* Scans use Ellora AI. Check your Universal Key balance for usage costs.</p>
      </div>

      {/* Scan Results */}
      {scanResults && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
          {(() => {
            const visibleOpps = (scanResults.opportunities || []).filter(o => !o.converted);
            return (
              <>
                <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-emerald-600" />
                  Latest Scan Results ({visibleOpps.length} opportunities found)
                </h2>
                <div className="space-y-4">
                  {visibleOpps.map((opp, visIdx) => {
                    // Map back to original index for convert (before filter)
                    const originalIndex = (scanResults.opportunities || []).findIndex((o, i) => !o.converted && (() => {
                      let cnt = -1;
                      for (let j = 0; j <= i; j++) if (!(scanResults.opportunities[j].converted)) cnt++;
                      return cnt === visIdx;
                    })());
                    // simpler: compute original index directly
                    let origIdx = -1;
                    let cnt = -1;
                    for (let j = 0; j < (scanResults.opportunities || []).length; j++) {
                      if (!scanResults.opportunities[j].converted) cnt++;
                      if (cnt === visIdx) { origIdx = j; break; }
                    }
                    return (
                      <div key={origIdx} className="border border-slate-200 rounded-sm p-5 hover:border-[#3b4872] transition-colors bg-slate-50">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-display font-bold text-lg text-[#0F172A]">
                                {opp.client_name || opp.company || 'Company Name Not Available'}
                              </h3>
                              {opp.confidence === 'cross-verified' && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-display font-bold uppercase tracking-wider" title={`Confirmed by: ${(opp.source_providers || []).join(', ')}`}>
                                  ✓ Verified by {(opp.source_providers || []).length} sources
                                </span>
                              )}
                              {/* Change 6: EC badge */}
                              {opp.signal_type === 'environmental_clearance' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-display font-bold uppercase tracking-wider" title="Environmental clearance filed — pre-construction signal">
                                  EC Filed
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-3">
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Investment:</span>
                                <span className="font-display font-bold text-emerald-700">
                                  ₹{opp.investment_crores || opp.estimated_value_crores || 'N/A'} Crore
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Location:</span>
                                <span className="text-slate-700">{opp.location || 'Not available'}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Land Size:</span>
                                <span className="text-slate-700">{opp.land_size_acres ? `${opp.land_size_acres} Acres` : 'Not available'}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Project Status:</span>
                                <span className={`tag ${
                                  opp.project_status?.includes('Construction started') ? 'bg-blue-100 text-blue-700' :
                                  opp.project_status?.includes('Planning') ? 'bg-amber-100 text-amber-700' :
                                  opp.project_status?.includes('Operational') ? 'bg-emerald-100 text-emerald-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>{opp.project_status || 'Not available'}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Date Announced:</span>
                                <span className="text-slate-700">
                                  {opp.date_announced || opp.announcement_date
                                    ? _fmtD(opp.date_announced || opp.announcement_date)
                                    : 'Not available'}
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-slate-600 min-w-[140px]">Industry:</span>
                                <span className="text-slate-700">{opp.industry || 'N/A'}</span>
                              </div>
                            </div>

                            {opp.brief_description &&
                             !opp.brief_description.trim().startsWith('```') &&
                             !opp.brief_description.trim().startsWith('[') &&
                             !opp.brief_description.trim().startsWith('{') && (
                              <div className="mt-3 p-3 bg-white rounded border border-slate-200">
                                <p className="text-sm text-slate-700 leading-relaxed">{opp.brief_description}</p>
                              </div>
                            )}

                            {opp.source_url && (
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                <div className="flex items-start gap-2">
                                  <ExternalLink size={16} className="text-[#3b4872] mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-slate-600 mb-1">Source:</div>
                                    <a href={opp.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#3b4872] hover:underline break-all block">
                                      {opp.source_url}
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-slate-200">
                          <button onClick={() => convertToLead(scanResults.scan_id, origIdx)} className="btn-primary text-xs flex items-center gap-2">
                            Convert to Lead
                          </button>
                          <button onClick={() => handleEnrich(opp)} disabled={enriching === opp} className="btn-secondary text-xs flex items-center gap-2">
                            {enriching === opp ? (<><Loader2 size={14} className="animate-spin" /> Enriching...</>) : (<><Sparkles size={14} /> Enrich Info</>)}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {visibleOpps.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-sm">All opportunities in this scan have been converted to leads.</div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Previous Scans */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h2 className="font-display font-bold text-lg mb-4">Previous Scans</h2>
        <div className="space-y-2">
          {scans.slice(0, 10).map(scan => {
            const isDone = !scan.status || scan.status === 'done';
            const isRunning = scan.status === 'running' || scan.status === 'pending';
            const isFailed = scan.status === 'failed';
            return (
              <div
                key={scan.id}
                onClick={isDone ? async () => {
                  try {
                    const detail = await apiGet(`/crm/opportunity-finder/scans/${scan.id}`);
                    setScanResults(detail);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } catch { toast.error('Failed to load scan details'); }
                } : undefined}
                className={`flex items-center justify-between p-3 border border-slate-200 rounded-sm text-sm transition-colors ${
                  isDone ? 'cursor-pointer hover:bg-slate-50 hover:border-[#3b4872]' : 'cursor-default opacity-80'
                }`}
                data-testid={`scan-row-${scan.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isRunning && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold shrink-0">
                      <Loader2 size={10} className="animate-spin" /> {scan.progress || 'Running…'}
                    </span>
                  )}
                  {isFailed && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold shrink-0">
                      Failed
                    </span>
                  )}
                  <div className="min-w-0">
                    <span className="font-display font-semibold">{scan.results_count ?? 0} opportunities</span>
                    <span className="text-slate-500 mx-2">·</span>
                    <span className="text-slate-600 truncate">{(scan.industries || []).join(', ')}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-500 shrink-0 ml-2">{_fmtDT(scan.scan_date)}</span>
              </div>
            );
          })}
          {scans.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-500">No previous scans. Start your first scan above!</div>
          )}
        </div>
      </div>

      {/* Convert to Lead Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Convert to Lead</h2>
              <button onClick={() => { setShowConvertModal(false); setOwnerName(''); setOwnerId(''); }} className="text-slate-500 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleConvertSubmit} className="p-6 space-y-4">
              <div>
                <label className="label">Assign Lead Owner *</label>
                <EmployeePicker
                  value={ownerName}
                  onChange={(name, id) => { setOwnerName(name || ''); setOwnerId(id || ''); }}
                  placeholder="Search by name or emp ID…"
                  allowFreeText={false}
                  testid="convert-lead-owner"
                  required
                />
                <p className="text-xs text-slate-500 mt-2">This opportunity will be converted to a lead and assigned to this person.</p>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={() => { setShowConvertModal(false); setOwnerName(''); setOwnerId(''); }} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!ownerName.trim()}>Convert to Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enrichment Modal */}
      {showEnrichModal && enrichedData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm shadow-xl max-w-3xl w-full my-8">
            <div className="bg-white border-b border-slate-200 p-6 flex items-center justify-between sticky top-0">
              <h2 className="font-display text-xl font-bold flex items-center gap-2">
                <Sparkles size={22} className="text-amber-500" /> Enriched Information
              </h2>
              <button onClick={() => { setShowEnrichModal(false); setEnrichedData(null); }} className="text-slate-500 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Decision Makers */}
              {enrichedData.decision_makers && enrichedData.decision_makers.length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-lg mb-3 text-[#3b4872]">Decision Makers</h3>
                  <div className="space-y-2">
                    {enrichedData.decision_makers.map((person, i) => (
                      <div key={i} className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                        <div className="font-semibold text-[#0F172A]">{person.name}</div>
                        <div className="text-sm text-slate-600">{person.designation} • {person.company}</div>
                        {person.contact_hint && <div className="text-xs text-slate-500 mt-1">Contact: {person.contact_hint}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Influencers */}
              {enrichedData.influencers && enrichedData.influencers.length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-lg mb-3 text-[#3b4872]">Influencers</h3>
                  <div className="space-y-2">
                    {enrichedData.influencers.map((person, i) => (
                      <div key={i} className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                        <div className="font-semibold text-[#0F172A]">{person.name}</div>
                        <div className="text-sm text-slate-600">{person.designation} • {person.company}</div>
                        {person.contact_hint && <div className="text-xs text-slate-500 mt-1">Contact: {person.contact_hint}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommenders */}
              {enrichedData.recommenders && enrichedData.recommenders.length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-lg mb-3 text-[#3b4872]">Recommenders</h3>
                  <div className="space-y-2">
                    {enrichedData.recommenders.map((person, i) => (
                      <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                        <div className="font-semibold text-[#0F172A]">{person.name}</div>
                        <div className="text-sm text-slate-600">{person.designation} • {person.company}</div>
                        {person.contact_hint && <div className="text-xs text-slate-500 mt-1">Contact: {person.contact_hint}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consultants */}
              {enrichedData.consultants && enrichedData.consultants.length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-lg mb-3 text-[#3b4872]">Consultants</h3>
                  <div className="space-y-2">
                    {enrichedData.consultants.map((firm, i) => (
                      <div key={i} className="p-3 bg-purple-50 border border-purple-200 rounded-sm">
                        <div className="font-semibold text-[#0F172A]">{firm.firm_name}</div>
                        <div className="text-sm text-slate-600">Role: {firm.role}</div>
                        {firm.contact && <div className="text-xs text-slate-500 mt-1">Contact: {firm.contact}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {enrichedData.note && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
                  <p className="text-sm text-slate-600">{enrichedData.note}</p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 p-6">
              <button onClick={() => { setShowEnrichModal(false); setEnrichedData(null); }} className="btn-primary w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
